/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { useMemo } from "react";

import type { SemanticIntent } from "./use-design-tokens";
import { useDesignTokens } from "./use-design-tokens";

import type { SemanticSurfaceShadeConfig } from "./use-design-tokens";

export interface SurfacePalette {
  surface: ReturnType<
    ReturnType<typeof useDesignTokens>["getSemanticSurfaceColors"]
  >;
  interaction: ReturnType<
    ReturnType<typeof useDesignTokens>["getSemanticInteractionColors"]
  >;
  solidSurface?: ReturnType<
    ReturnType<typeof useDesignTokens>["getSemanticSurfaceColors"]
  >;
}

/**
 * Hook to build a palette of surface and interaction colors for a given intent.
 * Reduces boilerplate by combining surface and interaction colors in one call.
 *
 * @param intent - The semantic intent (e.g., 'primary', 'warning', 'success')
 * @param surfaceOverrides - Optional overrides for surface color shades
 * @param solidSurfaceOverrides - Optional overrides for solid surface variant
 * @returns Object containing surface, interaction, and optionally solidSurface colors
 */
export function useSurfacePalette(
  intent: SemanticIntent,
  surfaceOverrides?: Partial<SemanticSurfaceShadeConfig>,
  solidSurfaceOverrides?: Partial<SemanticSurfaceShadeConfig>,
): SurfacePalette {
  const { getSemanticSurfaceColors, getSemanticInteractionColors } =
    useDesignTokens();

  return useMemo(() => {
    const surface = getSemanticSurfaceColors(
      intent,
      "on-light",
      surfaceOverrides,
    );
    const interaction = getSemanticInteractionColors(intent);

    const solidSurface = solidSurfaceOverrides
      ? getSemanticSurfaceColors(intent, "on-light", solidSurfaceOverrides)
      : undefined;

    return {
      surface,
      interaction,
      solidSurface,
    };
  }, [
    intent,
    getSemanticSurfaceColors,
    getSemanticInteractionColors,
    surfaceOverrides,
    solidSurfaceOverrides,
  ]);
}

/**
 * Hook to build multiple palettes at once for different intents.
 * Useful when a component needs multiple color palettes.
 *
 * @param intents - Record of intent names to their configs
 * @returns Record of intent names to their palettes
 */
export function useMultipleSurfacePalettes(
  intents: Record<
    string,
    {
      intent: SemanticIntent;
      surfaceOverrides?: Partial<SemanticSurfaceShadeConfig>;
      solidSurfaceOverrides?: Partial<SemanticSurfaceShadeConfig>;
    }
  >,
): Record<string, SurfacePalette> {
  const { getSemanticSurfaceColors, getSemanticInteractionColors } =
    useDesignTokens();

  return useMemo(() => {
    const palettes: Record<string, SurfacePalette> = {};

    for (const [key, config] of Object.entries(intents)) {
      const surface = getSemanticSurfaceColors(
        config.intent,
        "on-light",
        config.surfaceOverrides,
      );
      const interaction = getSemanticInteractionColors(config.intent);

      const solidSurface = config.solidSurfaceOverrides
        ? getSemanticSurfaceColors(
            config.intent,
            "on-light",
            config.solidSurfaceOverrides,
          )
        : undefined;

      palettes[key] = {
        surface,
        interaction,
        solidSurface,
      };
    }

    return palettes;
  }, [intents, getSemanticSurfaceColors, getSemanticInteractionColors]);
}
